import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormBuilderFields,
  FunnelStepHeader,
  FunnelWithHelper,
  StepperFooter,
  buildFormValuesFromFields,
  buildPayloadFromFields,
} from '@folio/components';
import { isFieldVisible } from '@folio/components/forms/form-builder/conditionalVisibilityUtils';
import { SectionAutoFillButton } from '@folio/components/forms/form-builder/FormBuilder';
import { getResolver } from '@folio/components/forms/form-builder/getZodResolverFromFields';
import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { ASSET_BATCH_LIMIT, MAX_FLEET_SIZE } from '@folio/constants';
import { parseCsv, useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { shouldShowAutoFill } from '@folio/utils';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import {
  clientWithFork,
  getKorintErrorCode,
  getNewBranchIdAfterFork,
  getKorintErrorDetails,
  keyValueFieldsToObject,
  useAssetControllerBatchCreateAssets,
  useAssetControllerBatchEditAssets,
  useAssetControllerBatchRemoveAssets,
  useAssetControllerCreateAsset,
  useAssetControllerEditAsset,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import type { Field, FieldShortcut } from 'korint-sdk/react';
import { useResolvedEntityFields } from '../shared-fields/use-entity-fields';
import { fieldColumns, fieldFilters, fieldLabel } from '../shared-fields/field-table-columns';
import { chunk } from 'lodash';
import { CalendarClock, Download, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

/**
 * @debt MVP is handling searching / filtering / sorting of assets in the frontends
 */

type AssetValues = Record<string, unknown>;
type FleetAsset = { id: string; name?: string; values: AssetValues; createdOnBranch: boolean };
type ImportError = { row?: number; field?: string; message: string };
// `interrupted` means the request never came back: the batch may well have landed
type ImportFailure = { kind: 'rejected' | 'interrupted'; errors: ImportError[] };

const normalizeHeader = (value: string) => value.trim().toLowerCase();

const toCsvCell = (value: string) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

// All-or-nothing: validate every CSV row with the same resolver the form uses;
// return typed batch items only when nothing fails.
async function buildAssetsFromCsv({
  csvRows,
  fields,
  labelForField,
  resolver,
}: {
  csvRows: Record<string, string>[];
  fields: Field[];
  labelForField: (field: Field) => string;
  resolver: (values: AssetValues) => Promise<{ errors: Record<string, { message: string }> }>;
}): Promise<{ items: AssetValues[] } | { errors: ImportError[] }> {
  const headerToField = new Map<string, Field>();
  for (const field of fields) {
    headerToField.set(normalizeHeader(labelForField(field)), field);
    headerToField.set(normalizeHeader(field.name), field);
  }

  const errors: ImportError[] = [];
  const items: AssetValues[] = [];

  for (const [index, csvRow] of csvRows.entries()) {
    const rowNumber = index + 2; // header occupies line 1
    const values: AssetValues = {};
    for (const [header, cell] of Object.entries(csvRow)) {
      const field = headerToField.get(normalizeHeader(header));
      if (field && cell !== '') {
        values[field.name] = cell;
      }
    }

    const { errors: rowErrors } = await resolver(values);
    for (const [field, error] of Object.entries(rowErrors)) {
      errors.push({ row: rowNumber, field, message: error.message });
    }
    if (Object.keys(rowErrors).length === 0) {
      const visibleFields = fields.filter(field => isFieldVisible(field, values));
      const payload = buildPayloadFromFields(visibleFields, values);
      items.push({ name: values.registrationNumber, ...payload });
    }
  }

  return errors.length > 0 ? { errors } : { items };
}

type ServerBatchFailure = { index: number; message: string };

// A rejected batch reports every failing item (BATCH_OPERATION_FAILED); map them back to CSV lines.
function toImportErrors(error: unknown, chunkStartRow: number): ImportError[] | null {
  if (getKorintErrorCode(error) !== 'BATCH_OPERATION_FAILED') {
    return null;
  }
  const details = getKorintErrorDetails(error) as { failures?: ServerBatchFailure[] } | undefined;
  if (!details?.failures?.length) {
    return null;
  }
  return details.failures.map(({ index, message }) => ({ row: chunkStartRow + index, message }));
}

type FleetAssetFunnelModuleProps = {
  fields?: FieldShortcut[];
  mode?: 'stepper' | 'standalone';
  title?: string;
  subtitle?: string;
  path: string;
  description?: string;
  helper?: React.ReactNode;
};

function AssetFormDialog({
  open,
  onClose,
  fields,
  asset,
  onSaved,
  save,
  title,
}: {
  open: boolean;
  onClose: () => void;
  fields: Field[];
  asset?: FleetAsset;
  onSaved: () => void;
  save: (values: AssetValues, assetId?: string) => Promise<void>;
  title: string;
}) {
  const { t } = useTranslation();
  const form = useForm<AssetValues>({
    mode: 'all',
    defaultValues: asset?.values ?? buildFormValuesFromFields(fields, {}),
    resolver: getResolver(fields),
  });

  async function onSubmit(values: AssetValues) {
    const visibleFields = fields.filter(field => isFieldVisible(field, values));
    const payload = buildPayloadFromFields(visibleFields, values);
    await save(payload, asset?.id);
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <Group justify="between" gap={2}>
            <DialogTitle>{title}</DialogTitle>
            {shouldShowAutoFill() && <SectionAutoFillButton fields={fields} />}
          </Group>
        </DialogHeader>

        <Form {...form}>
          <Stack asChild>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormBuilderFields fields={fields} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={form.formState.isSubmitting}>
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Stack>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function FleetAssetFunnelModule({
  fields: rawFields = [],
  mode = 'stepper',
  title,
  subtitle,
  path,
  description,
  helper,
}: FleetAssetFunnelModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();
  const { policy, workingBranch } = usePolicyOnDashboard();
  const stepper = useStepperContext();

  const [editing, setEditing] = useState<{ asset?: FleetAsset } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    ids: string[];
    clearSelection?: () => void;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [forkedBranchId, setForkedBranchId] = useState<string>();
  const activeBranchId = forkedBranchId ?? branchId;

  const {
    data: assets,
    isLoading: isAssetsLoading,
    refetch: refetchAssets,
  } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
      branchId: activeBranchId,
      count: MAX_FLEET_SIZE,
    }),
    enabled: !!policyId,
    select: data =>
      data.data
        .filter(({ status }) => status !== 'ASSET_STOPPED')
        .map(asset => ({
          ...asset,
          customFields: keyValueFieldsToObject(asset.customFields ?? []),
        })),
  });

  const {
    fields,
    payloadFields,
    isLoading: isProductConfigLoading,
  } = useResolvedEntityFields({
    entityType: 'asset',
    productId: policy.productId,
    fields: rawFields,
  });

  const { mutateAsync: createAsset } = useAssetControllerCreateAsset({
    client: { client: clientWithFork },
  });
  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    client: { client: clientWithFork },
  });
  const { mutateAsync: batchRemoveAssets } = useAssetControllerBatchRemoveAssets({
    client: { client: clientWithFork },
  });
  const { mutateAsync: batchCreateAssets } = useAssetControllerBatchCreateAssets({
    // Import failures are listed row by row in the dialog below; the generic toast would double-report.
    mutation: { meta: { disableDefaultErrorHandling: true } },
    client: { client: clientWithFork },
  });
  const { mutateAsync: batchEditAssets } = useAssetControllerBatchEditAssets({
    client: { client: clientWithFork },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importFailure, setImportFailure] = useState<ImportFailure | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState<{
    ids: string[];
    clearSelection?: () => void;
  } | null>(null);

  const isMta = workingBranch?.branchType === 'MTA';
  // MTA: assets predating the branch keep their start date and cannot be removed
  const isFullyEditable = (asset?: { createdOnBranch?: boolean }) =>
    !isMta || Boolean(asset?.createdOnBranch);

  const startedAtField = payloadFields.find(({ name }) => name === 'startedAt');
  const labelForField = (field: Field) => fieldLabel(t, field);

  // Returns the branch the next call must target: `activeBranchId` is render-scoped, so a
  // chunked loop has to thread the fork itself instead of waiting for the state update.
  function trackFork(response: object, currentBranchId = activeBranchId) {
    const newBranchId = getNewBranchIdAfterFork(response);
    if (newBranchId) {
      setForkedBranchId(newBranchId);
    }
    return newBranchId ?? currentBranchId;
  }

  async function saveAsset(values: AssetValues, assetId?: string) {
    const name = values.registrationNumber as string | undefined;
    if (assetId) {
      const { startedAt, ...keptFields } = values;
      const data = isFullyEditable(editing?.asset) ? { name, ...values } : { name, ...keptFields };
      trackFork(await updateAsset({ assetId, params: { branchId: activeBranchId }, data }));
    } else {
      trackFork(
        await createAsset({
          params: { branchId: activeBranchId },
          data: { policyId, name, ...values },
        }),
      );
    }
    await refetchAssets();
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    const { ids, clearSelection } = pendingDelete;
    setIsDeleting(true);
    try {
      trackFork(
        await batchRemoveAssets({
          params: { branchId: activeBranchId },
          data: { policyId, assetIds: ids },
        }),
      );
      clearSelection?.();
      await refetchAssets();
      setPendingDelete(null);
    } finally {
      setIsDeleting(false);
    }
  }

  async function applyBulkStartDate(values: AssetValues) {
    if (!bulkStartDate) {
      return;
    }
    const { ids, clearSelection } = bulkStartDate;
    let batchBranchId = activeBranchId;
    for (const batch of chunk(ids, ASSET_BATCH_LIMIT)) {
      batchBranchId = trackFork(
        await batchEditAssets({
          params: { branchId: batchBranchId },
          data: { policyId, assets: batch.map(assetId => ({ assetId, ...values })) },
        }),
        batchBranchId,
      );
    }
    clearSelection?.();
    await refetchAssets();
  }

  function downloadTemplate() {
    const header = payloadFields.map(field => toCsvCell(labelForField(field))).join(',');
    const blob = new Blob([`${header}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'assets-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function importCsv(file: File) {
    setIsImporting(true);
    try {
      const { rows: csvRows } = parseCsv(await file.text());
      if (csvRows.length === 0) {
        setImportFailure({
          kind: 'rejected',
          errors: [{ message: t('assetFunnel.import.empty') }],
        });
        return;
      }

      const result = await buildAssetsFromCsv({
        csvRows,
        fields: payloadFields,
        labelForField,
        resolver: getResolver(payloadFields),
      });
      if ('errors' in result) {
        setImportFailure({ kind: 'rejected', errors: result.errors });
        return;
      }

      try {
        let batchBranchId = activeBranchId;
        for (const [batchIndex, batch] of chunk(result.items, ASSET_BATCH_LIMIT).entries()) {
          try {
            batchBranchId = trackFork(
              await batchCreateAssets({
                params: { branchId: batchBranchId },
                data: { policyId, assets: batch },
              }),
              batchBranchId,
            );
          } catch (error) {
            // header occupies line 1
            const chunkStartRow = batchIndex * ASSET_BATCH_LIMIT + 2;
            const rejected = toImportErrors(error, chunkStartRow);
            setImportFailure(
              rejected
                ? { kind: 'rejected', errors: rejected }
                : {
                    kind: 'interrupted',
                    errors: [{ message: t('assetFunnel.import.interrupted') }],
                  },
            );
            return;
          }
        }
      } finally {
        // reflect assets created before a mid-loop failure
        await refetchAssets();
      }
    } finally {
      setIsImporting(false);
    }
  }

  const rows: FleetAsset[] = (assets ?? []).map(asset => ({
    id: asset.id,
    name: asset.name,
    values: buildFormValuesFromFields(fields, asset),
    createdOnBranch: Boolean(asset.createdOnBranch),
  }));

  const columns: ColumnDef<FleetAsset>[] = [
    ...fieldColumns<FleetAsset>({ fields: payloadFields, labelForField }),
    {
      id: 'actions',
      meta: { sticky: 'right' },
      cell: ({ row }) => (
        <Group
          align="stretch"
          justify="end"
          gap={1}
          className="opacity-60 transition-opacity group-hover:opacity-100"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => setEditing({ asset: row.original })}
            aria-label={t('common.edit')}
          >
            <Pencil className="size-4" />
          </Button>
          {isFullyEditable(row.original) && (
            <Button
              type="button"
              variant="destructive-ghost"
              size="icon"
              className="size-8"
              onClick={() => setPendingDelete({ ids: [row.original.id] })}
              aria-label={t([`${policy.productId}.assetFunnel.remove`, 'assetFunnel.remove'])}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </Group>
      ),
    },
  ];

  if (isAssetsLoading || isProductConfigLoading) {
    return (
      <Center className="h-32">
        <div className="text-sm text-muted-foreground">{t('assetFunnel.loading')}</div>
      </Center>
    );
  }

  const filters = fieldFilters({ fields: payloadFields, labelForField });
  const hasAssets = rows.length > 0;
  const dialogFields =
    editing?.asset && !isFullyEditable(editing.asset)
      ? fields.map(field => (field.name === 'startedAt' ? { ...field, disabled: true } : field))
      : fields;

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={
          title ?? t([`${policy.productId}.assetFunnel.${path}.title`, 'funnelSteps.asset.title'])
        }
        subtitle={subtitle ?? description ?? t('funnelSteps.asset.subtitle', '')}
      />

      <FunnelWithHelper helper={helper}>
        <Stack gap={3}>
          <DataTable
            columns={columns}
            data={rows}
            dense
            searchable
            filters={filters}
            enableRowSelection
            getRowId={row => row.id}
            renderSelectionActions={(selected, { clearSelection, selectAll, filteredCount }) => {
              const editableIds = selected.filter(isFullyEditable).map(({ id }) => id);
              return (
                <Group gap={2}>
                  {selected.length < filteredCount ? (
                    <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
                      {t('dataTable.selectAll')} ({filteredCount})
                    </Button>
                  ) : (
                    <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                      {t('dataTable.deselectAll')}
                    </Button>
                  )}
                  {startedAtField && editableIds.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkStartDate({ ids: editableIds, clearSelection })}
                    >
                      <CalendarClock className="mr-1.5 size-4" />
                      {t('assetFunnel.bulkStartDate')} ({editableIds.length})
                    </Button>
                  )}
                  {editableIds.length > 0 && (
                    <Button
                      type="button"
                      variant="destructive-ghost"
                      size="sm"
                      onClick={() => setPendingDelete({ ids: editableIds, clearSelection })}
                    >
                      <Trash2 className="mr-1.5 size-4" />
                      {t('common.delete')} ({editableIds.length})
                    </Button>
                  )}
                </Group>
              );
            }}
          />

          <Stack gap={2} className="sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing({})}
              className="flex-1 border-dashed"
            >
              <Plus className="mr-2 size-4" />
              {t([`${policy.productId}.assetFunnel.addAnother`, 'assetFunnel.addAnother'])}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              loading={isImporting}
            >
              <Upload className="mr-2 size-4" />
              {t('assetFunnel.import.button')}
            </Button>
            <Button type="button" variant="ghost" onClick={downloadTemplate}>
              <Download className="mr-2 size-4" />
              {t('assetFunnel.import.template')}
            </Button>
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) {
                void importCsv(file);
              }
            }}
          />
        </Stack>
      </FunnelWithHelper>

      <StepperFooter
        showPreviousButton
        showNextButton={mode === 'stepper'}
        nextButtonEnabled={hasAssets}
        onNext={() => stepper?.next(forkedBranchId ? { branchId: forkedBranchId } : undefined)}
      />

      {editing && (
        <AssetFormDialog
          key={editing.asset?.id ?? 'new'}
          open
          onClose={() => setEditing(null)}
          fields={dialogFields}
          asset={editing.asset}
          save={saveAsset}
          onSaved={() => setEditing(null)}
          title={editing.asset ? t('assetFunnel.editVehicle') : t('assetFunnel.addVehicle')}
        />
      )}

      {bulkStartDate && startedAtField && (
        <AssetFormDialog
          key="bulk-start-date"
          open
          onClose={() => setBulkStartDate(null)}
          fields={[startedAtField]}
          save={applyBulkStartDate}
          onSaved={() => setBulkStartDate(null)}
          title={t('assetFunnel.bulkStartDateTitle', { count: bulkStartDate.ids.length })}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={isOpen => {
          if (!isOpen && !isDeleting) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(pendingDelete?.ids.length ?? 0) > 1
                ? t('assetFunnel.deleteManyTitle')
                : t('assetFunnel.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('assetFunnel.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <Button variant="destructive" loading={isDeleting} onClick={confirmDelete}>
              {t('common.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!importFailure} onOpenChange={isOpen => !isOpen && setImportFailure(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {importFailure?.kind === 'interrupted'
                ? t('assetFunnel.import.interruptedTitle')
                : t('assetFunnel.import.rejectedTitle')}
            </DialogTitle>
          </DialogHeader>
          <Stack asChild gap={1}>
            <ul className="text-sm">
              {(importFailure?.errors ?? []).map((error, index) => {
                const field = payloadFields.find(({ name }) => name === error.field);
                return (
                  <li key={index} className="text-muted-foreground">
                    {error.row != null && (
                      <Text inline weight="medium" tone="default">
                        {t('assetFunnel.import.line', { line: error.row })}
                        {field ? ` · ${labelForField(field)}` : ''} —{' '}
                      </Text>
                    )}
                    {error.message}
                  </li>
                );
              })}
            </ul>
          </Stack>
          <DialogFooter>
            <Button type="button" onClick={() => setImportFailure(null)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
